import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import useFlatAllowanceCalculations from 'components/hooks/quotes/useFlatAllowanceCalculations'
import ActionButtons from 'components/quote_explorer/ActionButtons'
import { ChecklistCard } from 'components/quote_explorer/ChecklistCard'
import { type QuoteExplorerMode } from 'components/quote_explorer/QuoteExplorer'
import { TotalCostBreakdownCard } from 'components/quote_explorer/TotalCostBreakdownCard'
import { useQuotingEmployeeContext } from 'components/quoting/employees/QuotingEmployeeContext'
import { useQuotePricing } from 'components/quoting/quotes/pricing/QuotePricingProvider'
import { AllowanceStrategies } from 'pages/employer_onboarding/quote/types'
import { usePutQuotingPricesQuoteId as useUpdateQuotePricing } from 'utils/web_api'
import { type QuotingQuoteResult } from 'utils/web_api.schemas'

import { type Allowance, type Allowances } from './FlatAllowanceExplorer'

type Props = {
  quoteId: string
  prospectName: string
  employees?: QuotingQuoteResult[]
  isLoadingEmployees: boolean
  savedAllowances: Allowance[]
  acceptQuoteUrl?: string
  mode: QuoteExplorerMode
  canOnboardFromQuote: boolean
  canShareQuote: boolean
  onClickSaveQuote: () => void
  prospectAssociatedWithEmployer: boolean
  hideDownloadLink?: boolean
  canSendEditorInvites: boolean
  canSendOnboardInvites: boolean
  canEditQuote: boolean
}

function SidePanel ({
  quoteId,
  prospectName,
  employees,
  isLoadingEmployees,
  savedAllowances,
  acceptQuoteUrl,
  mode,
  canOnboardFromQuote,
  canShareQuote,
  onClickSaveQuote,
  prospectAssociatedWithEmployer,
  canSendEditorInvites,
  canSendOnboardInvites,
  canEditQuote,
}: Props) {
  const { watch, reset, formState: { isDirty } } = useFormContext<Allowances>()

  const allowances = watch('allowances')

  const { willEnrollEmployeeIds } = useQuotingEmployeeContext()

  const willEnrollEmployees = useMemo(() => {
    return employees?.filter((employee) => willEnrollEmployeeIds.has(employee.id)) ?? []
  }, [employees, willEnrollEmployeeIds])

  const {
    totalEmployerCost,
    totalEmployeeCost,
    currentTotalEmployerCost,
    currentTotalEmployeeCost,
    costsByEmployeeId,
  } = useFlatAllowanceCalculations(willEnrollEmployees, allowances)

  const { quotePricing } = useQuotePricing()
  const updateQuotePricingMutation = useUpdateQuotePricing()

  const hasCurrentCoverageData = useMemo(() => {
    if (!employees?.length) {
      return false
    }

    const numEmployeesWithCurrentCoverageData = employees.filter((employee) => employee.employee_medical_contribution !== null && employee.employer_medical_contribution !== null).length
    return (numEmployeesWithCurrentCoverageData / employees.length) >= 0.5
  }, [employees])

  const onClickDiscardChanges = () => {
    reset()
  }

  const onClickSaveQuoteInternal = () => {
    if (quotePricing) {
      updateQuotePricingMutation.mutate({
        quoteId,
        data: quotePricing,
      })
    }

    onClickSaveQuote()
  }

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <div className='flex flex-col flex-1 px-6 py-4 space-y-6 overflow-y-auto min-h-0'>
        <TotalCostBreakdownCard
          isLoading={isLoadingEmployees}
          currentPlanTotalEmployerMedicalContribution={currentTotalEmployerCost}
          currentPlanTotalEmployeeMedicalContribution={currentTotalEmployeeCost}
          totalMonthlyEmployerCost={totalEmployerCost}
          totalMonthlyEmployeeContribution={totalEmployeeCost}
          shouldDisplayComparisons={hasCurrentCoverageData}
        />
        <ChecklistCard
          isLoading={isLoadingEmployees}
          allowanceType={AllowanceStrategies.Flat}
          quoteResults={employees ?? []}
          calculatedCostsByEmployeeId={costsByEmployeeId}
        />
      </div>
      <div className='border-t border-gray-200 flex flex-col gap-2 p-4 flex-shrink-0'>
        <ActionButtons
          mode={mode}
          quoteId={quoteId}
          downloadLink={null}
          saveButtonDisabled={isLoadingEmployees}
          onClickSaveQuote={onClickSaveQuoteInternal}
          hasUnsavedChanges={isDirty || !savedAllowances.length}
          discardChanges={onClickDiscardChanges}
          prospectName={prospectName}
          acceptQuoteUrl={acceptQuoteUrl ?? null}
          prospectAssociatedWithEmployer={prospectAssociatedWithEmployer}
          canOnboardFromQuote={canOnboardFromQuote}
          canShareQuote={canShareQuote}
          canSendEditorInvites={canSendEditorInvites}
          canSendOnboardInvites={canSendOnboardInvites}
          canEditQuote={canEditQuote}
          hideDownloadLink
        />
      </div>
    </div>
  )
}

export default SidePanel
